import React, {
  Fragment,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Button } from 'components/ui/button';
import {
  IconEdit16,
  IconEmail16,
  IconPhone16,
  IconWhatsApp16,
} from '@app/icons';
import { useNavigate } from 'react-router-dom';
import { LayoutProfile } from '@app/layouts';
import { ProfileUserAdditionalInfo, ProfileUserInfo } from './components';
import { deleteUserFile, uploadUserFiles } from '@app/api';
import { User, UserType } from '@app/models';
import { ProfileAvatar } from 'pages/Profile/components';
import AboutUser from 'pages/Profile/components/AboutUser';
import { ModalReport } from '@app/modals';
import { useNotification, useUserContext } from '@app/providers';
import Reviews from '../Reviews/Reviews';
import { FilePicker, PortfolioList } from '@app/components';

interface Props {
  user: User;
  updateUser?: (user: Partial<User>) => void;
  editable?: boolean;
  showHeader?: boolean;
}

const NOT_SPECIFIED = 'Не указан';

function ProfileUser(props: Props) {
  const { user, updateUser, editable = false, showHeader = true } = props;
  const { showNotification } = useNotification();
  const { user: authUser, currentProfile, showSignInModal } = useUserContext();
  const navigate = useNavigate();
  const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);

  const onClickEdit = useCallback(() => {
    navigate('/profile/edit');
  }, [navigate]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onClickShare = useCallback(async () => {
    try {
      if (!authUser.authenticated) {
        showSignInModal();
        return;
      }
      const url = `${window.location.hostname}/user/${user?.id}`;
      await navigator.clipboard.writeText(url);
      showNotification({
        variant: 'success',
        message: 'Ссылка скопирована в буфер обмена',
      });
    } catch (e) {
      showNotification({
        variant: 'error',
        message: 'Ссылка не была скопирована',
      });
    }
  }, [authUser.authenticated, user?.id, showNotification, showSignInModal]);

  const onChangeFiles = useCallback(
    async (files: File[]) => {
      try {
        const response = await uploadUserFiles(user!.id, files);
        if (updateUser) {
          updateUser({ filesIds: response.filesIds });
        }
      } catch (e) {}
    },
    [user, updateUser]
  );

  const onDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        if (updateUser) {
          updateUser({
            filesIds: user
              .filesIds!.split(',')
              .filter((item) => item !== fileId)
              .join(','),
          });
        }
        await deleteUserFile(user.id, fileId);
        return true;
      } catch (e) {
        return false;
      }
    },
    [updateUser, user]
  );

  const onCloseReportModal = useCallback(() => {
    setReportModalVisible(false);
  }, []);

  const onClickReport = useCallback(() => {
    setReportModalVisible(true);
  }, []);

  const leftContent = useMemo(
    () => (
      <div className="flex flex-col items-center gap-4 w-full px-2 sm:px-0">
        <ProfileAvatar
          avatarFiles={
            !!user.avatarFilesIds ? user.avatarFilesIds.split(',')[0] : ''
          }
          editable={false}
          className="mx-auto"
        />
        <AboutUser
          id={user.id}
          ratingUser={user.rating}
          views={14}
          lastDateUpdate={9}
          phone={user.phone}
          email={user.email}
          instagram={user.instagram || NOT_SPECIFIED}
          telegram={user.telegram || NOT_SPECIFIED}
          phones={user.phones}
          status={user.status}
          whatAppNumber={user.whatsAppNumber}
          editable={editable}
          userType={user.type}
        />
      </div>
    ),
    [user, editable]
  );

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!user) return [];
    const buttons = [
      <Button
        key="share"
        disabled={currentProfile.blocked}
        onClick={onClickShare}
        variant="outline"
        className="flex-1 sm:flex-initial share-btn"
        size="sm"
      >
        Поделиться
      </Button>,
    ];
    if (currentProfile.id !== user.id) {
      buttons.push(
        <Button
          key="report"
          variant="destructive"
          onClick={onClickReport}
          disabled={currentProfile.blocked || user.id === authUser.id}
          className="flex-1 sm:flex-initial"
          size="sm"
        >
          Пожаловаться
        </Button>
      );
    }
    return buttons;
  }, [
    authUser.id,
    currentProfile.blocked,
    currentProfile.id,
    onClickReport,
    onClickShare,
    user,
  ]);

  const contactIcons = useMemo<ReactNode[]>(() => {
    if (!user) return [];
    const icons = [
      <a
        key="email"
        className="inline-flex items-center justify-center rounded p-3 hover:bg-accent transition border flex-shrink-0 w-10 h-10"
        href={`mailto:${user.email}`}
        title="Email"
      >
        <IconEmail16 className="w-4 h-4" />
      </a>,
    ];
    if (!!user.whatsAppNumber) {
      icons.push(
        <a
          key="wa"
          className="inline-flex items-center justify-center rounded p-3 hover:bg-accent transition border flex-shrink-0 w-10 h-10"
          href={`https://api.whatsapp.com/send?phone=${user.whatsAppNumber}`}
          title="WhatsApp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconWhatsApp16 className="w-4 h-4" />
        </a>
      );
    }
    icons.push(
      <a
        key="phone"
        className="inline-flex items-center justify-center rounded p-3 hover:bg-accent transition border flex-shrink-0 w-10 h-10"
        href={`tel:+${user.phone}`}
        title="Телефон"
      >
        <IconPhone16 className="w-4 h-4" />
      </a>
    );
    return icons;
  }, [user]);

  const rightContent = useMemo(
    () => <ProfileUserInfo user={user} editable={editable} />,
    [editable, user]
  );

  const rightControls = useMemo<ReactNode[]>(
    () => [
      <Button
        key="edit"
        onClick={onClickEdit}
        variant="outline"
        size="sm"
        className="action-link-fade shrink-0"
      >
        <IconEdit16 className="w-4 h-4 mr-2" />
        <span>Редактировать</span>
      </Button>,
    ],
    [onClickEdit]
  );

  return (
    <div className="w-full">
      <LayoutProfile
        pending={false}
        toolbarProps={
          showHeader
            ? {
                title: editable ? 'Личные данные' : 'Данные пользователя',
                rightControls: editable ? rightControls : [],
                onClickBack,
              }
            : null
        }
        leftContent={leftContent}
        rightContent={rightContent}
      >
        <Card className="w-full">
          <CardContent className="p-2 sm:p-4 lg:p-6">
            <Tabs defaultValue="0" className="w-full">
              <TabsList
                className={`flex flex-col md:grid w-full gap-1 h-auto p-1 bg-muted ${
                  editable
                    ? 'md:grid-cols-4'
                    : user.type === UserType.ENTITY
                    ? 'md:grid-cols-3'
                    : 'md:grid-cols-2'
                }`}
              >
                <TabsTrigger
                  value="0"
                  className="text-xs sm:text-sm px-3 py-3 data-[state=active]:bg-white whitespace-normal h-auto w-full justify-start md:justify-center"
                >
                  <span className="hidden lg:inline">
                    Профессиональные данные
                  </span>
                  <span className="lg:hidden">Проф. данные</span>
                </TabsTrigger>

                {editable && (
                  <TabsTrigger
                    value="1"
                    className="text-xs sm:text-sm px-3 py-3 data-[state=active]:bg-white whitespace-normal h-auto w-full justify-start md:justify-center"
                  >
                    <span className="hidden lg:inline">Документы и Файлы</span>
                    <span className="lg:hidden">Документы</span>
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="2"
                  className="text-xs sm:text-sm px-3 py-3 data-[state=active]:bg-white whitespace-normal h-auto w-full justify-start md:justify-center"
                >
                  <span className="hidden lg:inline">Опыт работы</span>
                  <span className="lg:hidden">Опыт</span>
                </TabsTrigger>

                {user.type === UserType.ENTITY && (
                  <TabsTrigger
                    value="3"
                    className="text-xs sm:text-sm px-3 py-3 data-[state=active]:bg-white whitespace-normal h-auto w-full justify-start md:justify-center"
                  >
                    Отзывы
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="0" className="mt-4 sm:mt-6">
                <div className="grid gap-4">
                  <ProfileUserAdditionalInfo user={user} />
                </div>
              </TabsContent>

              {editable && (
                <TabsContent value="1" className="mt-4 sm:mt-6">
                  <FilePicker
                    loadableFiles={
                      !!user.filesIds ? user.filesIds.split(',') : []
                    }
                    onChange={onChangeFiles}
                    editable={editable}
                    onDelete={onDeleteFile}
                  />
                </TabsContent>
              )}

              <TabsContent value="2" className="mt-4 sm:mt-6">
                <PortfolioList userId={user.id} editable={editable} />
              </TabsContent>

              {user.type === UserType.ENTITY && (
                <TabsContent value="3" className="mt-4 sm:mt-6">
                  <Reviews
                    userId={user.id}
                    showButton={currentProfile.id !== user.id}
                    userType={user.type}
                  />
                </TabsContent>
              )}
            </Tabs>
          </CardContent>

          {!editable && authUser.authenticated && (
            <div className="space-y-3 p-2 sm:p-4 lg:p-6 pt-0 pb-20 sm:pb-2">
              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
                {actionButtons.map((action, actionIndex) => (
                  <Fragment key={actionIndex}>{action}</Fragment>
                ))}
              </div>
              <div className="flex items-center justify-center sm:justify-end gap-2">
                {contactIcons.map((icon, iconIndex) => (
                  <Fragment key={iconIndex}>{icon}</Fragment>
                ))}
              </div>
            </div>
          )}
        </Card>
      </LayoutProfile>
      <ModalReport
        user={user}
        visible={reportModalVisible}
        onClose={onCloseReportModal}
      />
    </div>
  );
}

export default memo(ProfileUser);
